// The client's errors (contracts/client.md): an unknown operation or bad input (a programming error), an API error
// from the shared error envelope, a response that breaks its document (development and tests), and a network
// failure.

/** An operation id no published document declares. */
export class UnknownOperationError extends Error {
  override name = 'UnknownOperationError';
  /**
   * @param operationId the id that was asked for
   */
  constructor(readonly operationId: string) {
    super(`No published document declares the operation ${operationId}`);
  }
}

/** Input the operation cannot take: a missing path parameter or a query key its schema does not declare. */
export class ClientInputError extends Error {
  override name = 'ClientInputError';
}

/** The fields of the API's error envelope. */
export interface ErrorEnvelope {
  status: number;
  code: string;
  message: string;
  fieldErrors: Record<string, string>;
  requestId?: string;
  retryAfter?: number;
}

/** An error reply (4xx or 5xx) with the API's error envelope. */
export class ApiError extends Error implements ErrorEnvelope {
  override name = 'ApiError';
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;
  readonly requestId?: string;
  readonly retryAfter?: number;
  /**
   * @param envelope the envelope's fields, the status and the `retry-after` seconds
   */
  constructor(envelope: ErrorEnvelope) {
    super(envelope.message);
    this.status = envelope.status;
    this.code = envelope.code;
    this.fieldErrors = envelope.fieldErrors;
    this.requestId = envelope.requestId;
    this.retryAfter = envelope.retryAfter;
  }
}

/** A response that does not match the reply its operation declares for that status (FR-007). */
export class ContractError extends Error {
  override name = 'ContractError';
  /**
   * @param operationId the operation called
   * @param status the response status
   * @param instancePath where in the body the mismatch is
   * @param schemaPath which schema rule failed
   * @param detail what failed
   */
  constructor(
    readonly operationId: string,
    readonly status: number,
    readonly instancePath: string,
    readonly schemaPath: string,
    detail: string,
  ) {
    super(`${operationId} ${status}: ${instancePath || '(body)'} ${detail} (${schemaPath})`);
  }
}

/** The request never reached the API (offline, DNS, reset). */
export class NetworkError extends Error {
  override name = 'NetworkError';
  /**
   * @param operationId the operation called
   * @param cause the fetch failure
   */
  constructor(
    readonly operationId: string,
    cause: unknown,
  ) {
    super(`${operationId}: the API could not be reached`, { cause });
  }
}
